import { create } from "zustand"

import type { ContextExtra } from "~/server/api/routers/haki/types"

interface HakiBottomBarState {
  openThreadId: number | string | null
  /** Last rendered panel; retained while closed so an active subscription stays mounted. */
  mountedThreadId: number | string | null
  initialQuestion?: string
  initialExpanded?: boolean
  initialExtras?: ContextExtra[]
}

interface HakiBottomBarActions {
  openChat: (
    threadId: number | string,
    opts?: { question?: string; expanded?: boolean; extras?: ContextExtra[] },
  ) => void
  /**
   * Replace the provisional panel id, leaving the pending `initial*` fields intact.
   * Used when a chat opened with `"new"` is replaced by its real DB threadId
   * after pre-creation — we still want the initial question/extras to apply
   * on the remount.
   */
  setOpenThreadId: (threadId: number | string) => void
  closeChat: () => void
  /** Explicitly dispose the retained panel, e.g. when its bottom-bar tab is removed. */
  unmountChat: () => void
}

type HakiBottomBarStore = HakiBottomBarState & HakiBottomBarActions

export const useHakiBottomBarStore = create<HakiBottomBarStore>()((set) => ({
  openThreadId: null,
  mountedThreadId: null,
  initialQuestion: undefined,
  initialExpanded: undefined,
  initialExtras: undefined,
  openChat: (threadId, opts) =>
    set({
      openThreadId: threadId,
      mountedThreadId: threadId,
      initialQuestion: opts?.question,
      initialExpanded: opts?.expanded,
      initialExtras: opts?.extras,
    }),
  setOpenThreadId: (threadId) =>
    set((state) => ({
      openThreadId: state.openThreadId == null ? null : threadId,
      mountedThreadId: threadId,
    })),
  closeChat: () =>
    set({
      openThreadId: null,
      initialQuestion: undefined,
      initialExpanded: undefined,
      initialExtras: undefined,
    }),
  unmountChat: () => set({ mountedThreadId: null }),
}))
